/**
 * Avatar and Favicon utilities for CherryPulse
 * - MS Graph API profile photo fetching
 * - Agency favicon URL generation
 */

import type { IPublicClientApplication, AccountInfo } from '@azure/msal-browser';
import { graphRequest } from '@/config/msal.config';

/**
 * Fetch user profile photo from Microsoft Graph API
 * Returns a data URL (base64) that can be used as img src
 */
export async function fetchMsGraphProfilePhoto(
  msalInstance: IPublicClientApplication,
  account: AccountInfo
): Promise<string | null> {
  try {
    // Get access token for Graph API - use forceRefresh: false to avoid iframe issues
    const tokenResponse = await msalInstance.acquireTokenSilent({
      ...graphRequest,
      account,
      forceRefresh: false,
    });

    // Fetch profile photo from MS Graph
    const response = await fetch('https://graph.microsoft.com/v1.0/me/photo/$value', {
      headers: {
        Authorization: `Bearer ${tokenResponse.accessToken}`,
      },
    });

    if (!response.ok) {
      return null;
    }

    // Convert blob to data URL
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    // Silently fail - avatar fetch should never trigger login redirects
    return null;
  }
}

/**
 * Generate favicon URL for an agency website
 * Uses DuckDuckGo's icon service which reliably returns favicons
 * without 404 errors (returns a default icon if not found)
 *
 * @param websiteUrl - Full website URL (e.g., "https://example.com")
 */
export function getAgencyFaviconUrl(websiteUrl: string | undefined): string | null {
  if (!websiteUrl) return null;

  try {
    const url = new URL(websiteUrl);
    const domain = url.hostname;

    // DuckDuckGo's icon service - reliable, no 404 errors
    // Returns actual favicon or a default letter-based icon
    return `https://icons.duckduckgo.com/ip3/${domain}.ico`;
  } catch {
    return null;
  }
}

/**
 * Generate user avatar URL using unavatar.io as fallback
 * Uses email to fetch avatar from various social services
 *
 * @param email - User's email address
 */
export function getUserAvatarUrl(email: string | undefined): string | null {
  if (!email) return null;

  // unavatar.io can fetch avatars from Gravatar, GitHub, Twitter, etc. based on email
  return `https://unavatar.io/${email}?fallback=false`;
}

/**
 * Generate initials from a display name
 * Used as fallback when no profile photo is available
 */
export function getInitials(displayName: string): string {
  return displayName
    .split(' ')
    .map((part) => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}
